import {
  flip,
  FloatingOverlay,
  FloatingPortal,
  offset,
  Placement,
  ReferenceType,
  shift,
  useFloating,
} from "@floating-ui/react";
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  useContext,
  useLayoutEffect,
  useState,
  useMemo,
  SetStateAction,
} from "react";
import styled from "styled-components";
import {
  CreateTransient,
  LiElementProps,
  UlElementProps,
} from "../../shared/types";
import { Icon } from "../icons";
import { Typography } from "../typography";

type DropdownContextType = {
  isOpen: boolean;
  setIsOpen: Dispatch<React.SetStateAction<boolean>>;
  triggerWidth: number;
  setTriggerWidth: Dispatch<React.SetStateAction<number>>;
  floatingStyles?: React.CSSProperties;
  setReference: (node: ReferenceType | null) => void;
  setFloating: (node: HTMLElement | null) => void;
  selectedValue: string | null;
  setSelectedValue: Dispatch<SetStateAction<string | null>>;
  disabled?: boolean;
} & (
  | {
      label: string;
      id: string;
    }
  | {
      label?: never;
      id?: string;
    }
);

const DropdownContext = createContext<DropdownContextType>(
  {} as DropdownContextType,
);

type DropdownProps = PropsWithChildren & {
  onOpen?: () => void;
  onClose?: () => void;
  itemsPanelPlacement?: Placement;
  onValueChange?: (value: string) => void;
  value?: string | null;
  disabled?: boolean;
} & (
    | {
        label: string;
        id: string;
      }
    | {
        label?: undefined;
        id?: string;
      }
  );

// Note, this is not based on semantic html element, which is select and option
// and later should be investigated how to make it semantically correct for
// accessibility point of view.
export const Select = (props: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [triggerWidth, setTriggerWidth] = useState(0);
  const [selectedValue, setSelectedValue] = useState<string | null>(
    props.value ?? null,
  );

  if (props.value && props.value !== selectedValue) {
    setSelectedValue(props.value);
  }

  const { children, ...rest } = props;
  const { refs, elements, floatingStyles } = useFloating({
    placement: props.itemsPanelPlacement ?? "bottom-start",
    open: isOpen,
    onOpenChange: (open) => {
      if (open) rest.onOpen?.();
      else props.onClose?.();
      setIsOpen(open);
    },
    middleware: [
      // changes the placement of the floating element to keep it in view vertically.
      flip(),
      // changes the placement of the floating element to keep it in view horizontally.
      shift(),
      offset({ mainAxis: -10 }),
    ],
  });

  const contextValue = useMemo(() => {
    const base = {
      isOpen,
      setIsOpen,
      triggerWidth,
      setTriggerWidth,
      floatingStyles,
      setReference: refs.setReference,
      setFloating: refs.setFloating,
      selectedValue,
      setSelectedValue,
      disabled: props.disabled,
    };

    return props.label
      ? {
          ...base,
          label: props.label,
          id: props.id,
        }
      : base;
  }, [
    isOpen,
    triggerWidth,
    floatingStyles,
    refs.setReference,
    refs.setFloating,
    selectedValue,
    props.label,
    props.id,
    props.disabled,
  ]);

  useLayoutEffect(() => {
    if (elements.reference) {
      setTriggerWidth(elements.reference.getBoundingClientRect().width);
    }
  }, [elements.reference]);

  return (
    <DropdownContext.Provider value={contextValue}>
      {children}
    </DropdownContext.Provider>
  );
};

type SelectTriggerProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "value" | "onChange"
> & {
  onClearSelection?: () => void;
  placeholder?: string;
};

const SelectTrigger = ({
  onClearSelection,
  placeholder,
  children,
  ...rest
}: SelectTriggerProps) => {
  const {
    isOpen,
    setIsOpen,
    setReference,
    selectedValue,
    setSelectedValue,
    label,
    id,
    disabled,
  } = useContext(DropdownContext);

  return (
    <Wrapper disabled={disabled}>
      {label && <StyledLabel id={id}>{label}</StyledLabel>}
      <StyledButton
        ref={setReference}
        $isOpen={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-labelledby={label ? id : undefined}
        type="button"
        disabled={disabled}
        {...rest}
      >
        <ButtonContent>
          <SelectedTriggerDisplayValue placeholder={placeholder}>
            {children}
          </SelectedTriggerDisplayValue>
        </ButtonContent>
        {onClearSelection && selectedValue ? (
          <ContainerWithDivider
            onClick={(e) => {
              e.stopPropagation();
              setSelectedValue(null);
              onClearSelection?.();
            }}
          >
            <Icon.Close />
          </ContainerWithDivider>
        ) : null}
        <InputExpandIcon $isOpen={isOpen} />
      </StyledButton>
    </Wrapper>
  );
};

const ContainerWithDivider = styled.div`
  border: none;
  background: none;
  border-right: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
  display: grid;
  place-content: center;
  height: 24px;
  // fine tuned by Astrid
  // https://www.figma.com/design/QevqIXgzVebd8rWGM3wENC/Improvements?node-id=2416-23225&t=MZZQGdjPHVr96e6v-0
  padding: 0 4px 0 0;
  margin-right: 3px;
  &:hover {
    cursor: pointer;
  }
`;

const SelectedTriggerDisplayValue = ({
  placeholder,
  children,
}: PropsWithChildren<{ placeholder?: string }>) => {
  const { selectedValue } = useContext(DropdownContext);

  if (!selectedValue) return <PlaceholderText>{placeholder}</PlaceholderText>;

  return <>{children ?? selectedValue}</>;
};

const Wrapper = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.4;
    pointer-events: none;
  `}
`;

Select.Trigger = SelectTrigger;

const SelectOptions = ({ children, ...rest }: UlElementProps) => {
  const { isOpen, triggerWidth, setIsOpen, setFloating, floatingStyles } =
    useContext(DropdownContext);

  return isOpen ? (
    <>
      <FloatingOverlay
        onClick={() => {
          setIsOpen(false);
        }}
      />
      <FloatingPortal>
        <ItemsPanel
          ref={setFloating}
          style={floatingStyles}
          $triggerWidth={triggerWidth}
          {...rest}
        >
          {children}
        </ItemsPanel>
      </FloatingPortal>
    </>
  ) : null;
};

Select.Options = SelectOptions;

const SelectOptionsGroup = ({
  children,
  groupName,
}: PropsWithChildren<{ groupName: string }>) => {
  return (
    <StyledGroupContainer>
      <Typography variant="body-small-bold" color="GRAY_DARK">
        {groupName}
      </Typography>
      {children}
    </StyledGroupContainer>
  );
};

const StyledGroupContainer = styled.div`
  & > span {
    display: inline-block;
    padding: ${(props) => props.theme.SPACING.SM}
      ${(props) => props.theme.SPACING.MD};
  }

  & li {
    padding-left: ${(props) => props.theme.SPACING.LG};
  }
`;

Select.OptionsGroup = SelectOptionsGroup;

const SelectOption = ({
  children,
  onClick,
  value,
  ...rest
}: LiElementProps & {
  onClick?: () => void;
  value?: string;
}) => {
  const { setIsOpen, selectedValue, setSelectedValue } =
    useContext(DropdownContext);

  return (
    <Item
      onClick={() => {
        setIsOpen(false);
        if (value) {
          setSelectedValue(value);
        }
        onClick?.();
      }}
      $isSelected={value === selectedValue}
      {...rest}
    >
      {children}
    </Item>
  );
};

Select.Option = SelectOption;

const StyledButton = styled.button<CreateTransient<{ isOpen?: boolean }>>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid
    ${({ theme, $isOpen }) =>
      $isOpen ? theme.COLORS.GRAY : theme.COLORS.GRAY_MEDIUM};
  border-radius: 4px;
  color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
  font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
  font-size: ${({ theme }) => theme.FONT.SIZE.BODY};
  font-weight: ${({ theme }) => theme.FONT.WEIGHT.REGULAR};
  background-color: ${({ theme }) => theme.COLORS.WHITE};
  cursor: pointer;
  text-align: left;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    outline: none;
    border-color: ${({ theme }) => theme.COLORS.GRAY};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.COLORS.PRIMARY};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
  }
`;

const ButtonContent = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PlaceholderText = styled.span`
  color: ${({ theme }) => theme.COLORS.DARK_GREY};
`;

const StyledLabel = styled.label`
  font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
  font-size: ${({ theme }) => theme.FONT.SIZE.EXTRA_SMALL};
  font-weight: ${({ theme }) => theme.FONT.WEIGHT.REGULAR};
  color: ${({ theme }) => theme.COLORS.GRAY_DARK};
`;

const ItemsPanel = styled.ul<
  CreateTransient<{
    triggerWidth?: number;
  }>
>`
  // when used inside a MUI modal, that modal has z-index: 1300, so we need to be above that.
  z-index: 1500;
  background-color: ${({ theme }) => theme.COLORS.WHITE};
  padding: 0;
  list-style: none;
  max-height: 200px;
  overflow-y: auto;
  border-radius: 4px;
  box-shadow: 0px 2px 8px rgba(29, 27, 27, 0.2);
  min-width: ${({ $triggerWidth }) =>
    $triggerWidth ? `${$triggerWidth}px` : "auto"};
`;

const Item = styled.li<CreateTransient<{ isSelected?: boolean }>>`
  font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
  font-size: ${({ theme }) => theme.FONT.SIZE.SMALL};
  font-weight: ${({ theme, $isSelected }) =>
    $isSelected ? theme.FONT.WEIGHT.BOLD : theme.FONT.WEIGHT.REGULAR};
  padding: ${({ theme }) => `${theme.SPACING.SM} ${theme.SPACING.MD}`};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.COLORS.BLACK_GREY_OPACITY};
    text-transform: underline;
  }
`;

const InputExpandIcon = styled(Icon.Expand)<
  CreateTransient<{ isOpen: boolean }>
>`
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease-in-out;
  pointer-events: none;
`;
